import { Effect, Schema as S } from "effect";
import * as Command from "foldkit/command";
import posthog, { type BeforeSendFn } from "posthog-js";
import Rollbar from "rollbar";

type Env = Readonly<{
	mode: string;
	posthogToken?: string | undefined;
	posthogHost?: string | undefined;
	rollbarToken?: string | undefined;
}>;

const APP_VERSION = "v2";

export type Handles = Readonly<{
	rollbar: Rollbar | null;
	posthog: boolean;
}>;

/**
 * Session replay's network capture: bearer headers never leave the app, and
 * neither does a query string -- `/api/records/lookup?value=<LinkedIn URL>`
 * and `/api/records?search=<name>` carry other people's data (R17).
 */
export const scrubCapturedRequest = <
	T extends { requestHeaders?: Record<string, string>; name?: string },
>(
	request: T,
): T => {
	const { Authorization, authorization, ...headers } =
		request.requestHeaders ?? {};
	void Authorization;
	void authorization;
	const name =
		typeof request.name === "string"
			? request.name.split("?")[0]
			: request.name;
	return { ...request, requestHeaders: headers, name };
};

/** Rollbar's own defaults plus the two query params the capture page sends (R17). */
export const rollbarScrubFields = [
	"pw",
	"pass",
	"password",
	"secret",
	"confirm_password",
	"password_confirmation",
	"auth_token",
	"token",
	"api_key",
	"access_token",
	"accessToken",
	"value",
	"search",
];

const automaticInteractions = new Set([
	"$autocapture",
	"$rageclick",
	"$dead_click",
	"$copy_autocapture",
]);

/** Keep automatic interaction events disabled even if remote SDK settings change. */
export const dropAutocaptureEvents: BeforeSendFn = (event) =>
	event && automaticInteractions.has(event.event) ? null : event;

/**
 * Same projects as v1, tagged `app_version: v2` (KTD14). Everything is a
 * no-op when its env var is absent, as in v1's dev mode. Bearer tokens never
 * leave the app: headers are scrubbed and bodies are not captured.
 */
export const initAnalytics = (env: Env): Handles => {
	const posthogEnabled = Boolean(env.posthogToken);
	if (env.posthogToken) {
		posthog.init(env.posthogToken, {
			...(env.posthogHost ? { api_host: env.posthogHost } : {}),
			defaults: "2026-05-30",
			capture_exceptions: true,
			// UI interactions use explicit product events.
			autocapture: false,
			rageclick: false,
			capture_dead_clicks: false,
			// Record names, notes, input values, and link attributes can be personal.
			mask_all_text: true,
			mask_all_element_attributes: true,
			before_send: dropAutocaptureEvents,
			debug: env.mode === "development",
			session_recording: {
				recordHeaders: false,
				recordBody: false,
				maskCapturedNetworkRequestFn: scrubCapturedRequest,
			},
		});
		posthog.register({ app_version: APP_VERSION });
	}

	const rollbar =
		env.rollbarToken && env.mode !== "development"
			? new Rollbar({
					accessToken: env.rollbarToken,
					environment: env.mode,
					captureUncaught: true,
					captureUnhandledRejections: true,
					scrubHeaders: ["Authorization"],
					scrubFields: rollbarScrubFields,
					autoInstrument: {
						network: true,
						networkRequestHeaders: false,
						networkResponseHeaders: false,
						networkRequestBody: false,
						networkResponseBody: false,
					},
					payload: { app_version: APP_VERSION },
				})
			: null;

	return { rollbar, posthog: posthogEnabled };
};

const SIGNUP_WINDOW_MS = 10 * 60_000;
/** A device clock slightly behind Clerk's yields a negative age; tolerate it. */
const CLOCK_SKEW_MS = 60_000;

/**
 * KTD2: a user created within the last 10 minutes whose persistence carries no
 * `signed_up_at` super property is a fresh signup. `createdAt` is Clerk's
 * `user.createdAt` as an ISO string (or null).
 */
export const shouldEmitSignup = (
	createdAt: string | null,
	now: number,
	alreadyFlagged: boolean,
): boolean => {
	if (alreadyFlagged || createdAt === null) return false;
	const age = now - Date.parse(createdAt);
	return age >= -CLOCK_SKEW_MS && age <= SIGNUP_WINDOW_MS;
};

/** After sign-in, as v1's `App.tsx` effect: only id and email, never content. */
export const identify = (
	handles: Handles,
	user: Readonly<{ id: string; email: string; createdAt: string | null }>,
): void => {
	if (handles.posthog) {
		posthog.identify(user.id, { email: user.email });
		const now = Date.now();
		if (
			shouldEmitSignup(
				user.createdAt,
				now,
				Boolean(posthog.get_property("signed_up_at")),
			)
		) {
			const signed_up_at = new Date(now).toISOString();
			posthog.capture("user_signed_up");
			posthog.register_once({ signed_up_at });
			posthog.people.set_once({ signed_up_at });
		}
	}
	handles.rollbar?.configure({
		payload: { person: { id: user.id, email: user.email } },
	});
};

/** A product event; a no-op until `initAnalytics` ran with a token (`posthog.__loaded`). */
export const track = (event: string, props?: Record<string, unknown>): void => {
	if (posthog.__loaded) posthog.capture(event, props);
};

/**
 * A page-level `Track` Command: pages cannot see the boot-time handles, so the
 * gate is PostHog's own loaded flag. `completed` is the page Message it settles to.
 */
export const makeTrack = <Msg extends S.Top>(
	completed: Msg & (() => S.Schema.Type<Msg>),
) =>
	Command.define("Track", {
		args: { event: S.String, props: S.Record(S.String, S.String) },
		messages: [completed],
		execute: ({ event, props }) =>
			Effect.sync(() => track(event, props)).pipe(Effect.as(completed())),
	});

export const noopAnalytics: Handles = {
	rollbar: null,
	posthog: false,
};
